import { useQuery } from '@tanstack/react-query';
import type { GridReport } from '../../types/report';

export type { GridReport };

/**
 * Fields the report body cannot render without. A browser that cached a
 * response from an older deploy would otherwise hand us a payload missing
 * half its keys and crash the tab on first access.
 */
const REQUIRED_KEYS: (keyof GridReport)[] = [
  'period', 'energy', 'monthly', 'outages', 'caveats', 'restorationCurve',
];

/**
 * The report aggregates the entire history and is rebuilt server-side on a slow
 * cadence, so the client holds it for an hour. The request still revalidates on
 * every fetch — the server's own cache makes that cheap, and it means a schema
 * change is never served from a stale browser cache.
 */
export function useReport() {
  return useQuery({
    queryKey: ['report'],
    queryFn: async (): Promise<GridReport> => {
      const res = await fetch('/api/report', { cache: 'no-cache' });
      if (!res.ok) throw new Error('Failed to load the grid report');

      const report = (await res.json()) as GridReport;
      const missing = REQUIRED_KEYS.filter((k) => report[k] === undefined);
      if (missing.length > 0) {
        throw new Error(`Report data is out of date (missing ${missing.join(', ')}). Reload the page.`);
      }

      return report;
    },
    staleTime: 60 * 60 * 1000,
  });
}
